import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuCircleCheck,
  LuChevronLeft,
  LuChevronRight,
  LuClock3,
  LuDownload,
  LuEye,
  LuFileText,
  LuPlus,
  LuRefreshCw,
  LuRotateCcw,
  LuSearch,
  LuTruck,
  LuUserPlus,
} from "react-icons/lu";
import {
  businessRelocationSubmittedRfqPath,
  extractApiMessage,
  fetchBusinessRelocationRfqCities,
  fetchBusinessRelocationRfqStats,
  fetchBusinessRelocationRfqs,
} from "../../services/businessRelocationRfq.service";
import { fetchBusinessRelocationProviderAssignments } from "../../services/businessRelocationRfqProviderAssignment.service";
import BusinessRelocationAssignProvidersModal, {
  BusinessRelocationAssignedProvidersModal,
} from "../../components/logistics/BusinessRelocationAssignProvidersModal";
import LogisticsUserAssignModal from "../../components/logistics/LogisticsUserAssignModal";
import LogisticsRfqListScopeTabs from "../../components/logistics/LogisticsRfqListScopeTabs";
import LogisticsRfqSuperAdminNotice from "../../components/logistics/LogisticsRfqSuperAdminNotice";
import LogisticsTeamManagementNotice from "../../components/logistics/LogisticsTeamManagementNotice";
import { hasAssignedLogisticsUser } from "../../components/logistics/LogisticsRfqAssignedUserBadge";
import {
  canAssignLogisticsProvidersForRow as canStaffAssignProviders,
  canViewProviderAssignmentsForRow as canStaffViewProviders,
  fetchLogisticsStaffAccessUserId,
  isRfqAssignedToLogisticsManager,
  getRfqAssignedManagerAccessUserId,
} from "../../utils/logisticsRfqManagerAssignment";
import { patchRfqRowAfterUserAssignment } from "../../components/logistics/logisticsRfqUserAssignmentRowPatch";
import { isLogisticsUserAccount } from "../../utils/roleSwitcher";
import { buildLogisticsSubmittedRfqNavigationState } from "../../utils/logisticsRfqPostSubmitNavigate";
import "../../assets/styles/super_admin_access.scss";
import "../../assets/styles/business_relocation_rfqs_super_admin.scss";
import "../../assets/styles/logistics_rfq_super_admin_notice.scss";
import "../../assets/styles/business_relocation_rfq_provider_assign.scss";

const PAGE_OPTIONS = [10, 20, 50];

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In Review" },
  { value: "assigned", label: "Assigned" },
  { value: "closed", label: "Closed" },
];

const RELOCATION_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "office", label: "Office Relocation" },
  { value: "employee", label: "Employee Relocation" },
  { value: "both", label: "Both Office & Employee" },
];

function BusinessRelocationHeroSummaryArt() {
  return (
    <svg
      className="brrq-sa-hero__summary-art"
      viewBox="0 0 148 88"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M8 58h36v18H16c0-5 4-9 9-9h3V58zM44 58h52l10-16h16v16h8V58H8"
        fill="#f8fafc"
      />
      <path d="M10 62h128" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M18 52h10v10H18V52zm12 0h8v14h-8V52zm11 2h9v12h-9V54zm12-1h11v13H53V53z"
        fill="#ffe8d8"
        opacity="0.9"
      />
      <rect x="24" y="40" width="38" height="18" rx="3" fill="#ff6a00" />
      <path d="M62 40h18v18H62V40z" fill="#f8fafc" stroke="#e2e8f0" />
      <rect x="66" y="44" width="8" height="6" rx="1" fill="#dbeafe" />
      <circle cx="34" cy="62" r="5" fill="#475569" />
      <circle cx="34" cy="62" r="2" fill="#94a3b8" />
      <circle cx="72" cy="62" r="5" fill="#475569" />
      <circle cx="72" cy="62" r="2" fill="#94a3b8" />
      <path
        d="M108 24c0 7.2-5.8 13-13 13s-13-5.8-13-13 5.8-13 13-13 13 5.8 13 13z"
        fill="#ff6a00"
      />
      <circle cx="95" cy="24" r="4" fill="#fff" />
      <path
        d="M118 42h14v12h-14a2 2 0 01-2-2V44a2 2 0 012-2z"
        fill="#ffd8bf"
        stroke="#ffb07a"
        strokeWidth="1"
      />
      <path
        d="M126 42h10v12h-10a2 2 0 01-2-2V44a2 2 0 012-2z"
        fill="#ffe8d8"
        stroke="#ffc9a3"
        strokeWidth="1"
      />
      <path d="M120 48h6M123 45v6" stroke="#ff8a3d" strokeWidth="1.2" />
      <path d="M128 48h5M130.5 45.5v5" stroke="#ff8a3d" strokeWidth="1.2" />
    </svg>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const s = String(value).slice(0, 10);
  if (!s) return "—";
  const date = new Date(`${s}T00:00:00`);
  if (Number.isNaN(date.getTime())) return s;
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusClass(status) {
  const normalized = String(status || "pending").toLowerCase();
  return `sa-dispute-status sa-dispute-status--${normalized.replace(/\s+/g, "_")}`;
}

function assignmentBadge(type) {
  const normalized = String(type || "unassigned").toLowerCase();
  if (normalized === "manual") {
    return { label: "Manual", className: "sa-dispute-badge sa-dispute-badge--manual" };
  }
  if (normalized === "automatic") {
    return { label: "Auto", className: "sa-dispute-badge sa-dispute-badge--auto" };
  }
  return { label: "Unassigned", className: "sa-dispute-badge sa-dispute-badge--unassigned" };
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 5) {
  if (totalPages <= 1) return [1];
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function percentOf(part, total) {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function joinTooltipLines(...parts) {
  const lines = parts.map((part) => String(part || "").trim()).filter(Boolean);
  return lines.length ? lines.join("\n") : undefined;
}

function getBuyerName(row) {
  return row.buyerName || row.contactPerson || null;
}

function getBuyerMeta(row) {
  return row.companyName || row.buyerEmail || row.companyEmail || null;
}

function getBuyerTooltip(row) {
  return joinTooltipLines(
    getBuyerName(row),
    row.companyName,
    row.buyerEmail || row.companyEmail,
    row.contactNumber ? `Phone: ${row.contactNumber}` : null,
  );
}

function getAllocatedByLabel(row) {
  return (
    row.currentAssigneeName ||
    row.assignedLogisticsUserName ||
    row.assignedLogisticsManagerName ||
    row.assignedByName ||
    "—"
  );
}

function getAllocatedByRole(row) {
  return (
    row.currentAssigneeRole ||
    row.providerAssignmentOwnerRoleLabel ||
    (row.assignedLogisticsUserName ? "Logistics User" : null) ||
    (row.assignedLogisticsManagerName ? "Logistics Manager" : null) ||
    ""
  );
}

function getAllocatedByEmail(row) {
  const label = getAllocatedByLabel(row);
  if (label === "—") return null;
  if (row.assignedLogisticsUserName && label === String(row.assignedLogisticsUserName).trim()) {
    return row.assignedLogisticsUserEmail || null;
  }
  if (row.assignedLogisticsManagerName && label === String(row.assignedLogisticsManagerName).trim()) {
    return row.assignedLogisticsManagerEmail || null;
  }
  return row.assignedLogisticsUserEmail || row.assignedLogisticsManagerEmail || null;
}

function getAllocatedByMeta(row) {
  const role = getAllocatedByRole(row);
  const email = getAllocatedByEmail(row);
  if (role && email) return `${role} · ${email}`;
  return role || email || "—";
}

function getAllocatedByTooltip(row) {
  const label = getAllocatedByLabel(row);
  if (label === "—") return undefined;
  return joinTooltipLines(label, getAllocatedByRole(row), getAllocatedByEmail(row));
}

function getAssignedDate(row) {
  return row.assignedAt || row.assignedToUserAt || row.submittedDate || row.createdAt || null;
}

function getAllocatedByDisplay(row) {
  const label = getAllocatedByLabel(row);
  const role = getAllocatedByRole(row);
  if (label === "—") return "—";
  return role ? `${label} (${role})` : label;
}

export default function BusinessRelocationRfqsSuperAdminView({
  basePath = "/super-admin",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdminPortal = basePath === "/super-admin";
  const isLogisticsManagerPortal = basePath === "/dashboard/logistics-manager";
  const showListScopeTabs = isLogisticsManagerPortal && !isLogisticsUserAccount();
  const showLogisticsUserAssignment = showListScopeTabs;
  const showProviderAssignActions = isLogisticsManagerPortal;
  const submittedPortal = isSuperAdminPortal ? "super-admin" : "logistics-manager";
  const listReturnPath = isSuperAdminPortal
    ? `/super-admin/logistics/business-relocation-rfqs${location.search}`
    : `${basePath}/business-relocation-rfqs${location.search}`;
  const submittedPathOptions = useMemo(
    () => ({
      portal: submittedPortal,
      returnTo: listReturnPath,
    }),
    [submittedPortal, listReturnPath],
  );
  const submittedNavState = useMemo(
    () => buildLogisticsSubmittedRfqNavigationState(submittedPathOptions),
    [submittedPathOptions],
  );
  const dashboardPath = isSuperAdminPortal
    ? "/super-admin/dashboard"
    : `${basePath}/business-relocation-rfqs`;
  const listScope = searchParams.get("scope") === "mine" ? "mine" : "all";

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [relocationScope, setRelocationScope] = useState("");
  const [city, setCity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    thisMonth: 0,
  });
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [viewAssigned, setViewAssigned] = useState(null);
  const [viewAssignedLoading, setViewAssignedLoading] = useState(false);
  const [staffAccessUserId, setStaffAccessUserId] = useState(null);
  const [assignModalRfq, setAssignModalRfq] = useState(null);
  const [assignUserModalRfq, setAssignUserModalRfq] = useState(null);

  const scopeFilter = useMemo(
    () =>
      showListScopeTabs && listScope === "mine"
        ? { assignedToMe: true }
        : {},
    [showListScopeTabs, listScope],
  );

  const listFilters = useMemo(
    () => ({
      search: query.trim() || undefined,
      status: status || undefined,
      relocationScope: relocationScope || undefined,
      city: city || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      sortBy: "submittedDate",
      sortOrder: "desc",
      page,
      pageSize,
      ...scopeFilter,
    }),
    [query, status, relocationScope, city, dateFrom, dateTo, page, pageSize, scopeFilter],
  );

  const statsFilters = useMemo(
    () => ({
      search: query.trim() || undefined,
      status: status || undefined,
      relocationScope: relocationScope || undefined,
      city: city || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      ...scopeFilter,
    }),
    [query, status, relocationScope, city, dateFrom, dateTo, scopeFilter],
  );

  const canAssignProvidersForRow = (row) => {
    if (!isLogisticsManagerPortal) return true;
    return canStaffAssignProviders(row, staffAccessUserId);
  };

  const canViewProvidersForRow = (row) => {
    if (!isLogisticsManagerPortal) {
      return Number(row?.assignedProviderCount) > 0;
    }
    return canStaffViewProviders(row, staffAccessUserId);
  };

  const canAssignLogisticsUserForRow = (row) => {
    if (!isLogisticsManagerPortal) return false;
    if (Number(row?.assignedProviderCount) > 0) return false;
    const managerId = getRfqAssignedManagerAccessUserId(row);
    if (managerId && !isRfqAssignedToLogisticsManager(row, staffAccessUserId)) {
      return false;
    }
    return true;
  };

  const loadCities = useCallback(async () => {
    try {
      const data = await fetchBusinessRelocationRfqCities();
      setCities(Array.isArray(data.cities) ? data.cities : []);
    } catch {
      setCities([]);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchBusinessRelocationRfqStats(statsFilters);
      setStats({
        total: Math.trunc(Number(data.total) || 0),
        assigned: Math.trunc(Number(data.assigned) || 0),
        inProgress: Math.trunc(Number(data.inProgress) || 0),
        completed: Math.trunc(Number(data.completed) || 0),
        overdue: Math.trunc(Number(data.overdue) || 0),
        thisMonth: Math.trunc(Number(data.thisMonth) || 0),
      });
    } catch {
      setStats({
        total: 0,
        assigned: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        thisMonth: 0,
      });
    }
  }, [statsFilters]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBusinessRelocationRfqs(listFilters);
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not load RFQs."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [listFilters]);

  useEffect(() => {
    void loadCities();
  }, [loadCities]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (!isLogisticsManagerPortal) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const id = await fetchLogisticsStaffAccessUserId();
        if (!cancelled) setStaffAccessUserId(id);
      } catch {
        if (!cancelled) setStaffAccessUserId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLogisticsManagerPortal]);

  const viewIdParam = searchParams.get("viewId");
  useEffect(() => {
    if (!viewIdParam) return;
    const id = Number(viewIdParam);
    if (!Number.isFinite(id) || id < 1) return;
    navigate(businessRelocationSubmittedRfqPath(id, submittedPathOptions), {
      replace: true,
      state: submittedNavState,
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("viewId");
        return next;
      },
      { replace: true },
    );
  }, [viewIdParam, navigate, setSearchParams, submittedPathOptions, submittedNavState]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const pageNumbers = buildPageNumbers(page, totalPages);
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  const emptyListMessage =
    showListScopeTabs && listScope === "mine"
      ? "No RFQs are currently assigned to you."
      : "No Business Relocation RFQs found.";

  const handleViewRfq = (row) => {
    navigate(businessRelocationSubmittedRfqPath(row.id, submittedPathOptions), {
      state: submittedNavState,
    });
  };

  const handleViewAssigned = async (row) => {
    setViewAssigned({ rfq: row, providers: [] });
    setViewAssignedLoading(true);
    try {
      const data = await fetchBusinessRelocationProviderAssignments(row.id);
      setViewAssigned({ rfq: row, providers: data.providers || [] });
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not load assigned providers."));
      setViewAssigned(null);
    } finally {
      setViewAssignedLoading(false);
    }
  };

  const handleUserAssignmentSaved = (result, rfqId) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === rfqId ? patchRfqRowAfterUserAssignment(item, result) : item,
      ),
    );
    setAssignUserModalRfq(null);
  };

  const handleAssignmentsSaved = (result, rfqId, rfqRow) => {
    const count = result?.assignedProviderCount ?? 0;
    const providers = Array.isArray(result?.providers) ? result.providers : null;
    setItems((prev) =>
      prev.map((item) =>
        item.id === rfqId ? { ...item, assignedProviderCount: count } : item,
      ),
    );
    const row =
      rfqRow ??
      items.find((item) => item.id === rfqId) ??
      (assignModalRfq?.id === rfqId ? assignModalRfq : null);
    if (count > 0 && row) {
      if (providers) {
        setViewAssigned({ rfq: row, providers });
        setViewAssignedLoading(false);
      } else {
        void handleViewAssigned(row);
      }
    }
  };

  const handleListScopeChange = (nextScope) => {
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextScope === "mine") {
          next.set("scope", "mine");
        } else {
          next.delete("scope");
        }
        return next;
      },
      { replace: true },
    );
  };

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setRelocationScope("");
    setCity("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await fetchBusinessRelocationRfqs({
        ...statsFilters,
        sortBy: "submittedDate",
        sortOrder: "desc",
        page: 1,
        pageSize: 1000,
      });
      const rows = Array.isArray(data.items) ? data.items : [];
      const headers = [
        "RFQ ID",
        "Buyer",
        "Allocated By",
        "Relocation Type",
        "Current City",
        "Destination City",
        "Preferred Date",
        "Status",
        "Assignment",
        "Assigned Date",
      ];
      const csvLines = [
        headers.join(","),
        ...rows.map((row) =>
          [
            row.rfqId || row.rfqReference,
            row.buyerName || row.contactPerson || "",
            getAllocatedByDisplay(row),
            row.relocationType || "",
            row.currentCity || "",
            row.destCity || "",
            formatDate(row.preferredMoveDate),
            formatStatus(row.status),
            assignmentBadge(row.logisticsUserAssignmentType).label,
            formatDate(getAssignedDate(row)),
          ]
            .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
            .join(","),
        ),
      ];
      const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `business-relocation-rfqs-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} RFQ${rows.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not export RFQs."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="sa-access-page sa-dispute-page brrq-sa-page">
      <header className="sa-access-head brrq-sa-hero">
        <div className="brrq-sa-hero__main">
          <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
            <Link to={dashboardPath}>Dashboard</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">Business Relocation RFQs</span>
          </nav>
          <h1>Business Relocation RFQs</h1>
          <p>
            {isSuperAdminPortal ? (
              <>
                Review and monitor all business relocation requests submitted by buyers.
                <br />
                No provider quotations are created from this intake.
              </>
            ) : (
              <>
                Review buyer relocation requests assigned to your logistics team.
                <br />
                No provider quotations are created from this intake.
              </>
            )}
          </p>
        </div>
        <aside className="brrq-sa-hero__summary" aria-label="Total RFQs summary">
          <div className="brrq-sa-hero__summary-text">
            <span className="brrq-sa-hero__summary-label">Total RFQs</span>
            <strong className="brrq-sa-hero__summary-count">
              {stats.total.toLocaleString()}
            </strong>
            <span className="brrq-sa-hero__summary-trend">
              +{stats.thisMonth.toLocaleString()} this month
            </span>
          </div>
          <BusinessRelocationHeroSummaryArt />
        </aside>
      </header>

      {isSuperAdminPortal ? (
        <LogisticsRfqSuperAdminNotice show variant="panel" />
      ) : (
        <LogisticsTeamManagementNotice
          show={showLogisticsUserAssignment}
          basePath={basePath}
        />
      )}

      <section className="brrq-sa-stats" aria-label="RFQ statistics">
        <article className="brrq-sa-stat">
          <span className="brrq-sa-stat__icon brrq-sa-stat__icon--orange">
            <LuFileText aria-hidden />
          </span>
          <div>
            <strong>{stats.total.toLocaleString()}</strong>
            <span>Total RFQs</span>
            <small>All RFQs</small>
          </div>
        </article>
        <article className="brrq-sa-stat">
          <span className="brrq-sa-stat__icon brrq-sa-stat__icon--blue">
            <LuCircleCheck aria-hidden />
          </span>
          <div>
            <strong>{stats.assigned.toLocaleString()}</strong>
            <span>Assigned RFQs</span>
            <small>{percentOf(stats.assigned, stats.total)} of total</small>
          </div>
        </article>
        <article className="brrq-sa-stat">
          <span className="brrq-sa-stat__icon brrq-sa-stat__icon--green">
            <LuRefreshCw aria-hidden />
          </span>
          <div>
            <strong>{stats.inProgress.toLocaleString()}</strong>
            <span>In Progress</span>
            <small>{percentOf(stats.inProgress, stats.total)} of total</small>
          </div>
        </article>
        <article className="brrq-sa-stat">
          <span className="brrq-sa-stat__icon brrq-sa-stat__icon--purple">
            <LuCircleCheck aria-hidden />
          </span>
          <div>
            <strong>{stats.completed.toLocaleString()}</strong>
            <span>Completed</span>
            <small>{percentOf(stats.completed, stats.total)} of total</small>
          </div>
        </article>
        <article className="brrq-sa-stat">
          <span className="brrq-sa-stat__icon brrq-sa-stat__icon--red">
            <LuClock3 aria-hidden />
          </span>
          <div>
            <strong>{stats.overdue.toLocaleString()}</strong>
            <span>Overdue</span>
            <small>{percentOf(stats.overdue, stats.total)} of total</small>
          </div>
        </article>
      </section>

      <div className="brrq-sa-filters">
        <label className="brrq-sa-filters__search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search RFQ ID, Buyer, Company, City..."
          />
        </label>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={relocationScope}
          onChange={(e) => {
            setRelocationScope(e.target.value);
            setPage(1);
          }}
        >
          {RELOCATION_OPTIONS.map((opt) => (
            <option key={opt.value || "all-type"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Cities</option>
          {cities.map((entry) => (
            <option key={entry.value || entry.label} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
        <div className="brrq-sa-filters__date-range">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            aria-label="Date from"
          />
          <span>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            aria-label="Date to"
          />
        </div>
        <div className="brrq-sa-filters__actions">
          <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={resetFilters}>
            <LuRotateCcw aria-hidden /> Reset Filters
          </button>
          <button
            type="button"
            className="sa-access-btn sa-access-btn--primary"
            onClick={() => void handleExport()}
            disabled={exporting}
          >
            <LuDownload aria-hidden /> {exporting ? "Exporting…" : "Export"}
          </button>
        </div>
      </div>

      <section className="sa-access-card sa-dispute-card">
        {showListScopeTabs ? (
          <LogisticsRfqListScopeTabs activeScope={listScope} onChange={handleListScopeChange} />
        ) : null}
        <div className="sa-access-table-wrap">
          <table
            className={`sa-access-table sa-dispute-table brrq-sa-dispute-table${
              isLogisticsManagerPortal ? " brrq-sa-dispute-table--manager" : ""
            }`}
          >
            <thead>
              <tr>
                <th>RFQ ID</th>
                <th>Buyer</th>
                <th>Allocated By</th>
                <th>Relocation Type</th>
                <th>Current City</th>
                <th>Destination City</th>
                <th>Preferred Date</th>
                <th>Status</th>
                <th>Assignment</th>
                <th>Assigned Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="sa-access-table__empty">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={11} className="sa-access-table__empty">
                    {emptyListMessage}
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const assignBadge = assignmentBadge(row.logisticsUserAssignmentType);
                  const providerCount = Number(row.assignedProviderCount) || 0;
                  const providersLoading =
                    viewAssignedLoading && viewAssigned?.rfq?.id === row.id;
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="sa-dispute-id-cell">
                          <Link
                            to={businessRelocationSubmittedRfqPath(row.id, submittedPathOptions)}
                            state={submittedNavState}
                            className="brrq-sa-rfq-id-link"
                          >
                            {row.rfqId || row.rfqReference}
                          </Link>
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-support-user" title={getBuyerTooltip(row)}>
                          <span className="sa-dispute-avatar">
                            {nameInitials(getBuyerName(row))}
                          </span>
                          <div className="sa-dispute-stack">
                            <span className="sa-dispute-stack__title">
                              {getBuyerName(row) || "—"}
                            </span>
                            <span className="sa-dispute-stack__meta">
                              {getBuyerMeta(row) || "—"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-support-user" title={getAllocatedByTooltip(row)}>
                          <span className="sa-dispute-avatar">
                            {nameInitials(getAllocatedByLabel(row))}
                          </span>
                          <div className="sa-dispute-stack">
                            <span className="sa-dispute-stack__title">
                              {getAllocatedByLabel(row)}
                            </span>
                            <span className="sa-dispute-stack__meta">{getAllocatedByMeta(row)}</span>
                          </div>
                        </div>
                      </td>
                      <td>{row.relocationType || "—"}</td>
                      <td>{row.currentCity || "—"}</td>
                      <td>{row.destCity || "—"}</td>
                      <td>
                        <span className="sa-dispute-date">{formatDate(row.preferredMoveDate)}</span>
                      </td>
                      <td>
                        <span className={statusClass(row.status)}>{formatStatus(row.status)}</span>
                      </td>
                      <td>
                        <span className={assignBadge.className}>{assignBadge.label}</span>
                      </td>
                      <td>
                        <span className="sa-dispute-date">{formatDate(getAssignedDate(row))}</span>
                      </td>
                      <td>
                        <div className="sa-dispute-actions">
                          <button
                            type="button"
                            className="sa-dispute-icon-btn"
                            onClick={() => handleViewRfq(row)}
                            aria-label={`View ${row.rfqId || row.rfqReference}`}
                            title="View details"
                          >
                            <LuEye aria-hidden />
                          </button>
                          {canViewProvidersForRow(row) && providerCount > 0 ? (
                            <button
                              type="button"
                              className="sa-dispute-icon-btn"
                              onClick={() => void handleViewAssigned(row)}
                              disabled={providersLoading}
                              aria-label={`View ${providerCount} assigned provider${
                                providerCount === 1 ? "" : "s"
                              } for ${row.rfqId || row.rfqReference}`}
                              title={`View assigned providers (${providerCount})`}
                            >
                              <LuTruck aria-hidden />
                            </button>
                          ) : null}
                          {showLogisticsUserAssignment && canAssignLogisticsUserForRow(row) ? (
                            <button
                              type="button"
                              className="sa-dispute-icon-btn"
                              onClick={() => setAssignUserModalRfq(row)}
                              aria-label={
                                hasAssignedLogisticsUser(row)
                                  ? "Reassign logistics user"
                                  : "Assign logistics user"
                              }
                              title={
                                hasAssignedLogisticsUser(row)
                                  ? "Reassign logistics user"
                                  : "Assign logistics user"
                              }
                            >
                              <LuUserPlus aria-hidden />
                            </button>
                          ) : null}
                          {showProviderAssignActions && canAssignProvidersForRow(row) ? (
                            <button
                              type="button"
                              className="sa-dispute-icon-btn sa-dispute-icon-btn--accent"
                              onClick={() => setAssignModalRfq(row)}
                              aria-label="Assign logistics providers"
                              title="Assign logistics providers"
                            >
                              <LuPlus aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="sa-access-footer">
          <span>
            Showing {rangeStart} to {rangeEnd} of {total.toLocaleString()} RFQs
          </span>
          <div className="sa-access-pagination">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                className={n === page ? "is-active" : ""}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Rows per page"
            >
              {PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      <LogisticsUserAssignModal
        open={Boolean(assignUserModalRfq)}
        rfq={assignUserModalRfq}
        rfqKind="business"
        onClose={() => setAssignUserModalRfq(null)}
        onAssigned={handleUserAssignmentSaved}
      />

      <BusinessRelocationAssignProvidersModal
        open={Boolean(assignModalRfq)}
        rfq={assignModalRfq}
        onClose={() => setAssignModalRfq(null)}
        onSaved={(result) => {
          if (assignModalRfq?.id) {
            handleAssignmentsSaved(result, assignModalRfq.id, assignModalRfq);
          }
        }}
      />

      <BusinessRelocationAssignedProvidersModal
        open={Boolean(viewAssigned)}
        rfq={viewAssigned?.rfq}
        providers={viewAssigned?.providers}
        loading={viewAssignedLoading}
        onClose={() => setViewAssigned(null)}
      />
    </div>
  );
}
